import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from '@zauberware/react-i18n/web'
import { Tabs, Card } from 'antd'
import ExportTab from './ExportTab'
import SearchControls from './SearchControls'

const { TabPane } = Tabs

/**
 * Flexible TableControls component supporting multiple usage patterns:
 *
 * 1. Legacy mode: Fixed tabs for Reports (FiltersTabContent, ColumnSelectionTabContent, Export)
 * 2. Modern mode: Configurable tabs array with modal support
 *
 * @component
 * @param {Object} props - Component props.
 *
 * --- Legacy Props (for Reports) ---
 * @param {React.ComponentType} [props.FiltersTabContent] - Component to render in the Filters tab.
 * @param {React.ComponentType} [props.ColumnSelectionTabContent] - Component to render in the Column Selection tab.
 * @param {Object} [props.filtersTabProps] - Props to pass to the FiltersTabContent component.
 * @param {Object} [props.columnSelectionTabProps] - Props to pass to the ColumnSelectionTabContent component.
 * @param {Function} [props.onExport] - Callback function triggered when export action is performed.
 * @param {boolean} [props.loading] - Indicates if the export action is loading.
 *
 * --- Modern Props (for Views with Context) ---
 * @param {Array<Object>} [props.tabs] - Array of tab configurations. Each tab can have:
 *   - key: string (required) - Unique tab key
 *   - label: string (required) - Tab label (can use translation keys)
 *   - content: React.Element - Tab content component
 *   - modal: React.Element - Modal component (opens when tab is clicked)
 *   - icon: React.Element - Optional icon for the tab
 *
 * --- Search Props ---
 * @param {Object} [props.searchConfig] - Search configuration object:
 *   - value: string - Current search text
 *   - onChange: Function - Callback when search text changes
 *   - onSearch: Function - Callback when search is triggered
 *   - onReset: Function - Optional callback for reset button
 *   - placeholder: string - Optional placeholder text
 *   - width: number - Optional width in pixels (default: 200)
 *   - showResetButton: boolean - Whether to show reset button (default: true)
 *
 * --- General Props ---
 * @param {string} [props.translationNamespace] - Namespace for translation keys.
 * @param {string} [props.defaultActiveKey] - Default active tab key.
 * @param {string} [props.className] - Additional CSS class for the Card container.
 *
 * @returns {React.ReactElement} The rendered TableControls component.
 *
 * @example
 * // Legacy usage (Reports)
 * <TableControls
 *   FiltersTabContent={FiltersTab}
 *   ColumnSelectionTabContent={ColumnSelectionTab}
 *   onExport={handleExport}
 *   translationNamespace="reports"
 * />
 *
 * @example
 * // Modern usage (Views with Modal)
 * <TableControls
 *   tabs={[
 *     { key: 'filters', label: t('tabs.filters'), content: <FiltersTab /> },
 *     { key: 'add', label: t('tabs.add'), modal: <AddModal /> },
 *     { key: 'export', label: t('tabs.export'), content: <ExportTab /> }
 *   ]}
 *   searchConfig={{
 *     value: textSearch,
 *     onChange: setTextSearch,
 *     onSearch: handleSearch,
 *     onReset: handleReset
 *   }}
 *   translationNamespace="customers"
 * />
 */
const TableControls = ({
  // Legacy props for Reports
  FiltersTabContent,
  ColumnSelectionTabContent,
  filtersTabProps,
  columnSelectionTabProps,
  onExport,
  loading,
  // Modern props
  tabs,
  searchConfig,
  // General
  translationNamespace,
  defaultActiveKey,
  className
}) => {
  const { t } = useTranslation([translationNamespace, 'common'])
  const [activeKey, setActiveKey] = useState(defaultActiveKey || 'filters')

  // Determine if we're using legacy or modern mode
  const isLegacyMode = !tabs && (FiltersTabContent || ColumnSelectionTabContent)

  // Handle tab change - close modal when switching tabs
  const handleTabChange = useCallback(key => {
    setActiveKey(key)
  }, [])

  // Find the modal for the current active tab (modern mode only)
  const activeModal = useMemo(() => {
    if (isLegacyMode || !tabs) return null
    const activeTab = tabs.find(tab => tab.key === activeKey)
    return activeTab?.modal || null
  }, [isLegacyMode, tabs, activeKey])

  // Handle modal close - switch back to first tab
  const handleModalClose = useCallback(() => {
    if (tabs && tabs.length > 0) {
      setActiveKey(tabs[0].key)
    }
  }, [tabs])

  // Render SearchControls in tabBarExtraContent if searchConfig is provided
  const tabBarExtra = searchConfig ? (
    <SearchControls
      value={searchConfig.value}
      onChange={searchConfig.onChange}
      onSearch={searchConfig.onSearch}
      onReset={searchConfig.onReset}
      placeholder={searchConfig.placeholder}
      width={searchConfig.width}
      translationNamespace={translationNamespace}
      showResetButton={searchConfig.showResetButton !== false}
    />
  ) : null

  // Render legacy mode (for Reports)
  if (isLegacyMode) {
    return (
      <Card className={className}>
        <Tabs activeKey={activeKey} onChange={handleTabChange}>
          <TabPane tab={t('tabs.filters')} key="filters">
            <FiltersTabContent {...filtersTabProps} />
          </TabPane>
          <TabPane tab={t('tabs.columnSelection')} key="columnSelection">
            <ColumnSelectionTabContent {...columnSelectionTabProps} />
          </TabPane>
          <TabPane tab={t('tabs.export')} key="export">
            <ExportTab
              onExport={onExport}
              loading={loading}
              translationNamespace={translationNamespace}
            />
          </TabPane>
        </Tabs>
      </Card>
    )
  }

  // Render modern mode (for Views with configurable tabs)
  return (
    <>
      <Card className={className || 'mb-3'}>
        <Tabs activeKey={activeKey} onChange={handleTabChange} tabBarExtraContent={tabBarExtra}>
          {tabs?.map(tab => (
            <TabPane
              tab={
                tab.icon ? (
                  <span>
                    {tab.icon} {tab.label}
                  </span>
                ) : (
                  tab.label
                )
              }
              key={tab.key}
            >
              {tab.content || null}
            </TabPane>
          ))}
        </Tabs>
      </Card>

      {/* Render modal if active tab has one */}
      {activeModal &&
        React.cloneElement(activeModal, {
          open: true,
          onClose: handleModalClose
        })}
    </>
  )
}

TableControls.propTypes = {
  // Legacy props for Reports
  FiltersTabContent: PropTypes.elementType,
  ColumnSelectionTabContent: PropTypes.elementType,
  filtersTabProps: PropTypes.object,
  columnSelectionTabProps: PropTypes.object,
  onExport: PropTypes.func,
  loading: PropTypes.bool,
  // Modern props
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.element,
      modal: PropTypes.element,
      icon: PropTypes.element
    })
  ),
  searchConfig: PropTypes.shape({
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    onReset: PropTypes.func,
    placeholder: PropTypes.string,
    width: PropTypes.number,
    showResetButton: PropTypes.bool
  }),
  // General
  translationNamespace: PropTypes.string.isRequired,
  defaultActiveKey: PropTypes.string,
  className: PropTypes.string
}

export default TableControls
